import { dryFetch } from '../../utils/dryFetch';
import { getOwnerID } from '../../utils/DryCookies';
import track from '../../utils/trackers'; // Import track
import {
  hydrateNotebooksClientDatabase,
  writeSystemValue,
  writeNotebook, // Import writeNotebook to save locally after create/rename
} from '../offline/';
import logger from '../../logger';

export const fetchNotebooksFromCloud = async () => {
  const ownerID = getOwnerID();
  track.startTime('fetchNotebooks'); // Start timing

  try {
    const results = await dryFetch({
      name: 'fetchNotebooks',
      path: `/notebooks/${ownerID}`,
    });

    const completedTime = track.endTime('fetchNotebooks'); // End timing
    logger.info(
      {
        ownerID,
        time: completedTime,
      },
      'fetchNotebooks time'
    );
    track.time({
      category: 'notebooks',
      name: 'fetch_notebooks',
      value: completedTime,
      label: '',
    });

    writeSystemValue({
      name: 'defaultNotebook',
      data: results.default_notebook,
    });
    writeSystemValue({
      name: 'notebooksLastSynced',
      utcDateTime: results.utcDateTime,
    });
    const notebooksObject = {};
    results.notebooks.forEach((element) => {
      notebooksObject[element.notebook_id] = element;
    });
    await hydrateNotebooksClientDatabase({ notebooks: results.notebooks }); // Added await
    return notebooksObject;
  } catch (error) {
    logger.error(error, 'fetchNotebooksFromCloud failed'); // Added context to error log
    // Rethrow or handle error appropriately
    throw error; // Rethrowing the error is often better than returning it
  }
};

export const renameNotebookCloud = async ({ notebook_id, notebook_name }) => {
  const ownerId = getOwnerID(); // Not used in API call but good for logging/tracking

  try {
    track.startTime('renameNotebook'); // Start timing
    const updatedNotebookResponse = await dryFetch({
      name: 'renameNotebook',
      path: `/notebook/${notebook_id}`,
      options: {
        method: 'PUT',
        body: JSON.stringify({
          notebook_name,
        }),
      },
    });

    const completedTime = track.endTime('renameNotebook'); // End timing
    logger.info(
      {
        ownerID: ownerId, // Use ownerId for logging
        time: completedTime,
      },
      'renameNotebook time'
    );
    track.time({
      category: 'notebooks',
      name: 'rename_notebook',
      value: completedTime,
      label: 'success', // Added label
    });

    // Assuming the API returns the updated notebook object under the 'result' key
    const savedNotebook = updatedNotebookResponse.result;

    // Save the updated notebook to IndexedDB as well
    if (savedNotebook) {
      await writeNotebook({ notebook: savedNotebook });
      logger.info(
        { notebookId: savedNotebook.notebook_id },
        'renameNotebookCloud: Saved updated notebook to IndexedDB'
      );
    } else {
      logger.warn(
        'renameNotebookCloud: API did not return a notebook object in result.'
      );
    }

    return savedNotebook; // Return the actual notebook object
  } catch (error) {
    logger.error(error, 'renameNotebookCloud failed'); // Added context to error log
    track.exception('renameNotebookCloud failed', false); // Track non-fatal exception
    // Rethrow or handle error appropriately
    throw error; // Rethrowing the error is often better than returning it
  }
};

/**
 * Creates a new notebook in the cloud via API call.
 * @param {object} params - Parameters for creating the notebook.
 * @param {string} params.notebook_name - The name for the new notebook.
 * @returns {Promise<object>} The newly created notebook object from the API.
 * @throws {Error} If the API call fails or returns an unexpected response.
 */
export const createNotebookCloud = async ({ notebook_name }) => {
  const ownerId = getOwnerID(); // Get for logging/tracking

  try {
    track.startTime('createNotebook'); // Start timing

    const notebookObject = {
      notebook_name,
      app_created_with: 'drynotes_web', // Consistent with old Redux action
      owner_id: ownerId,
    };

    const response = await dryFetch({
      name: 'createNotebook', // Descriptive name for logging/tracking
      path: `/notebook`, // Assumed endpoint for POST
      options: {
        method: 'POST', // Correct HTTP method for creation
        body: JSON.stringify(notebookObject), // Send the data
      },
    });

    const completedTime = track.endTime('createNotebook'); // End timing
    logger.info(
      { ownerID: ownerId, time: completedTime },
      'createNotebook time'
    );
    track.time({
      category: 'notebooks',
      name: 'create_notebook', // Consistent naming
      value: completedTime,
      label: 'success', // Added label
    });

    // --- IMPORTANT ---
    // Adjust the key ('notebook' or 'result') based on your actual API response structure.
    // The old Redux action expected 'json.notebook'. Let's assume 'notebook'.
    const newNotebook = response.notebook;

    if (!newNotebook || !newNotebook.notebook_id) {
      logger.error(
        'createNotebookCloud: API response did not contain a valid notebook object.',
        response
      );
      throw new Error('Failed to create notebook: Invalid API response.');
    }

    // Save the newly created notebook to IndexedDB
    await writeNotebook({ notebook: newNotebook });
    logger.info(
      { notebookId: newNotebook.notebook_id },
      'createNotebookCloud: Saved new notebook to IndexedDB'
    );

    return newNotebook; // Return the created notebook object
  } catch (error) {
    logger.error(error, 'createNotebookCloud failed'); // Added context to error log
    track.exception('createNotebookCloud failed', false); // Track non-fatal exception
    // Rethrow or handle error appropriately
    throw error; // Re-throw for upstream handling (e.g., in XState machine)
  }
};
// --- END NEW FUNCTION ---
